import React, { useState, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import API_URL from '../config';
import logger from '../utils/logger';
import GuideModal from './GuideModal';
import './DesktopComponentStyles.css';


const ConcentrationGrid = ({ onComplete }) => {
  const [numbers, setNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [gridSize, setGridSize] = useState(6); // Default to 6x6 grid
  const [stats, setStats] = useState({
    bestTime: null,
    lastTime: null,
    averageTime: null,
    totalAttempts: 0
  });
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  // Format duration in MM:SS
  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format date to MM/DD/YY
  const formatDate = (date) => {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  // Format time to HH:MM AM/PM
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  // Generate random grid
  const generateGrid = () => {
    const totalCells = gridSize * gridSize;
    const nums = Array.from({ length: totalCells }, (_, i) => i + 1);
    for (let i = nums.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }
    setNumbers(nums);
  };

  // Fetch stats and history
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/concentration-grid/stats`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      logger.debug('Received stats and history data:', data);
      setStats(data.stats);
      setHistory(data.history);
    } catch (error) {
      logger.error('Error fetching stats:', error);
      setError('Failed to fetch statistics');
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Start new game
  const startGame = () => {
    generateGrid();
    setSelectedNumbers([]);
    setStartTime(Date.now());
    setEndTime(null);
    setIsPlaying(true);
  };

  // Handle number selection
  const handleNumberClick = async (number, index) => {
    if (!isPlaying) return;
    
    const nextNumber = selectedNumbers.length + 1;
    if (number === nextNumber) {
      const newSelected = [...selectedNumbers, number];
      setSelectedNumbers(newSelected);

      const totalCells = gridSize * gridSize;
      if (newSelected.length === totalCells) {
        const endTime = Date.now();
        setEndTime(endTime);
        setIsPlaying(false);

        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/concentration-grid/attempt`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              duration: endTime - startTime,
              gridSize: gridSize
            })
          });
          if (!response.ok) throw new Error('Failed to save attempt');
          fetchStats();
          
          // Call handleComplete to notify parent components
          handleComplete();
          
          // Also save a local record of the attempt for Dashboard to use
          const attemptsCount = parseInt(localStorage.getItem('concentrationGridAttempts') || '0');
          localStorage.setItem('concentrationGridAttempts', attemptsCount + 1);
          localStorage.setItem('concentrationGridLastPlayed', new Date().toISOString());
          
          logger.debug('Concentration grid game completed and saved');
        } catch (error) {
          logger.error('Error saving attempt:', error);
          setError('Failed to save attempt');
        }
      }
    }
  };

  // Clear history
  const clearHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/concentration-grid/attempts`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to clear history');
      fetchStats();
    } catch (error) {
      logger.error('Error clearing history:', error);
      setError('Failed to clear history');
    }
  };

  // Add onComplete to your completion handler
  const handleComplete = () => {
    // Mark first concentration game task as completed
    markConcentrationGameTaskCompleted();
    
    // Call the onComplete callback if provided
    if (onComplete) {
      onComplete();
    }
  };

  // Function to mark concentration game task as completed
  const markConcentrationGameTaskCompleted = () => {
    // Set localStorage flag
    localStorage.setItem('firstConcentrationGame', 'true');
    
    // Update the server if possible
    const token = localStorage.getItem('token');
    if (token) {
      try {
        fetch(`${API_URL}/api/users/starter-task`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            task: 'firstConcentrationGame',
            completed: true
          })
        }).catch(error => {
          logger.error('Error updating concentration game completion status on server:', error);
        });
      } catch (error) {
        logger.error('Error updating concentration game completion status:', error);
      }
    }
  };

  // Handle grid size change
  const handleGridSizeChange = (size) => {
    if (!isPlaying) {
      setGridSize(size);
    }
  };

  return (
    <>
      <GuideModal guideKey="Concentration Grid" />
      <div className="desktop-standard-container w-full max-w-lg mx-auto p-4">
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-3 rounded-lg shadow text-center">
          <div className="text-sm text-gray-600">Best Time</div>
          <div className="text-lg font-bold">{stats.bestTime ? formatDuration(stats.bestTime) : '--:--'}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow text-center">
          <div className="text-sm text-gray-600">Last Time</div>
          <div className="text-lg font-bold">{stats.lastTime ? formatDuration(stats.lastTime) : '--:--'}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow text-center">
          <div className="text-sm text-gray-600">Average Time</div>
          <div className="text-lg font-bold">{stats.averageTime ? formatDuration(stats.averageTime) : '--:--'}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow text-center">
          <div className="text-sm text-gray-600">Total Attempts</div>
          <div className="text-lg font-bold">{stats.totalAttempts}</div>
        </div>
      </div>

      {/* Grid Size Selection */}
      <div className="mb-4">
        <div className="text-center mb-2 font-medium">Select Grid Size:</div>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            onClick={() => handleGridSizeChange(4)}
            className={`px-3 py-1 rounded ${
              gridSize === 4 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } ${isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isPlaying}
          >
            4x4
          </button>
          <button
            onClick={() => handleGridSizeChange(5)}
            className={`px-3 py-1 rounded ${
              gridSize === 5 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } ${isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isPlaying}
          >
            5x5
          </button>
          <button
            onClick={() => handleGridSizeChange(6)}
            className={`px-3 py-1 rounded ${
              gridSize === 6 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } ${isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isPlaying}
          >
            6x6
          </button>
          <button
            onClick={() => handleGridSizeChange(8)}
            className={`px-3 py-1 rounded ${
              gridSize === 8 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            } ${isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isPlaying}
          >
            8x8
          </button>
        </div>
      </div>

      {/* Game Controls */}
      <div className="mb-6 text-center">
        <button
          onClick={startGame}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          disabled={isPlaying}
        >
          {isPlaying ? 'Game in Progress' : 'Start Game'}
        </button>
      </div>

      {/* Grid */}
      {isPlaying && (
        <div className={`grid gap-1 sm:gap-2 mb-6 ${
          gridSize === 4 ? 'grid-cols-4' : 
          gridSize === 5 ? 'grid-cols-5' : 
          gridSize === 8 ? 'grid-cols-8' :
          'grid-cols-6'
        } max-w-full overflow-x-auto`}>
          {numbers.map((number, index) => (
            <button
              key={index}
              onClick={() => handleNumberClick(number, index)}
              className={`
                aspect-square 
                flex 
                items-center 
                justify-center 
                ${gridSize === 8 ? 'text-xs sm:text-sm' : 'text-sm sm:text-lg'} 
                font-bold 
                rounded 
                ${selectedNumbers.includes(number) 
                  ? 'bg-green-500 text-white cursor-not-allowed' 
                  : 'bg-white hover:bg-gray-100 text-gray-800'}
                shadow
                transition-colors
                duration-200
                ${gridSize === 8 ? 'w-8 h-8 sm:w-10 sm:h-10' : 'w-10 h-10 sm:w-12 sm:h-12'}
              `}
              disabled={selectedNumbers.includes(number)}
            >
              {number}
            </button>
          ))}
        </div>
      )}

      {/* History Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">History</h2>
          {history.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-red-500 hover:text-red-700 flex items-center"
            >
              <Trash2 size={16} className="mr-1" />
              Clear History
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {history.map((attempt, index) => (
            <div key={index} className="bg-white p-3 rounded-lg shadow flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-600">{formatDate(attempt.date)}</div>
                <div className="text-xs text-gray-500">{formatTime(attempt.date)}</div>
                {attempt.gridSize && (
                  <div className="text-xs text-gray-500">{attempt.gridSize}x{attempt.gridSize} Grid</div>
                )}
              </div>
              <div className="text-lg font-bold">{formatDuration(attempt.duration)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

export default ConcentrationGrid;